/**
 * PetRegisterPanel — the assembled Schedule-4 register for one pet with
 * missing-field warnings (migration 0100). Rendered on the pet detail page.
 */
import { type PetRegister, getPetRegister } from '@/lib/api';
import { AlertTriangle, CheckCircle2, ClipboardCheck } from 'lucide-react';
import { useEffect, useState } from 'react';

export const REGISTER_FIELD_LABELS: Record<string, string> = {
  date_of_birth: 'Date of birth',
  sex: 'Sex',
  neutered: 'Neuter status',
  breed: 'Breed / description',
  microchip: 'Microchip number',
  owner_address: 'Owner address',
  owner_phone: 'Owner phone',
  emergency_contact: 'Owner emergency contact',
  local_emergency_contact: 'Local emergency contact',
  vet: 'Veterinary surgeon',
  insurance: 'Insurance',
  diet: 'Diet / feeding',
  vaccinations: 'Vaccination record',
  worming: 'Worming date',
  flea_treatment: 'Flea treatment date',
  vet_consent: 'Consent to veterinary treatment',
  waiver: 'Signed consent form (waiver)',
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const d = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
    if (Number.isNaN(d.getTime())) return value;
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(d);
  } catch {
    return value;
  }
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
      <dd className="num text-right text-sm text-foreground">
        {value && value.length > 0 ? value : '—'}
      </dd>
    </div>
  );
}

export function PetRegisterPanel({ petId }: { petId: string }) {
  const [register, setRegister] = useState<PetRegister | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPetRegister(petId)
      .then((res) => {
        if (!cancelled) setRegister(res.register);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load register');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [petId]);

  return (
    <section
      className="rounded-xl border border-border bg-surface"
      aria-labelledby="pet-register-heading"
      data-testid="pet-register-panel"
    >
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
        <h2 id="pet-register-heading" className="flex items-center gap-2 text-sm font-semibold">
          <ClipboardCheck
            className="h-4 w-4 text-muted-foreground"
            strokeWidth={1.75}
            aria-hidden
          />
          Register
        </h2>
        {register && register.missing.length === 0 && (
          <span className="flex items-center gap-1 rounded-full bg-success/10 px-2.5 py-0.5 text-[11px] font-medium text-success ring-1 ring-inset ring-success/25">
            <CheckCircle2 className="h-3 w-3" aria-hidden />
            Complete
          </span>
        )}
        {register && register.missing.length > 0 && (
          <span className="num flex items-center gap-1 rounded-full bg-warning/15 px-2.5 py-0.5 text-[11px] font-medium text-warning-foreground ring-1 ring-inset ring-warning/30">
            <AlertTriangle className="h-3 w-3" aria-hidden />
            {register.missing.length} missing
          </span>
        )}
      </div>

      {loading && <p className="px-5 py-4 text-sm text-muted-foreground">Loading register…</p>}
      {error && <p className="px-5 py-4 text-sm text-destructive">{error}</p>}

      {register && (
        <div className="px-5 py-4">
          {register.missing.length > 0 && (
            <div className="mb-4 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm">
              <p className="font-medium text-warning-foreground">Missing before an inspection</p>
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {register.missing.map((key) => (
                  <li
                    key={key}
                    className="rounded-full bg-background px-2 py-0.5 text-xs text-foreground ring-1 ring-inset ring-border"
                  >
                    {REGISTER_FIELD_LABELS[key] ?? key}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid gap-x-8 gap-y-2 md:grid-cols-2">
            <dl className="divide-y divide-border">
              <Row
                label="Species / breed"
                value={[register.pet.species, register.pet.breed].filter(Boolean).join(' · ')}
              />
              <Row label="Colour" value={register.pet.colour} />
              <Row label="Date of birth" value={formatDate(register.pet.dateOfBirth)} />
              <Row label="Sex" value={register.pet.sex} />
              <Row
                label="Neutered"
                value={
                  register.pet.neutered === null ? null : register.pet.neutered === 1 ? 'Yes' : 'No'
                }
              />
              <Row label="Microchip" value={register.pet.microchip} />
              <Row label="Owner" value={register.owner.name} />
              <Row label="Owner address" value={register.owner.address} />
              <Row label="Owner phone" value={register.owner.phone} />
              <Row
                label="Emergency contact"
                value={[register.emergencyContact.name, register.emergencyContact.phone]
                  .filter(Boolean)
                  .join(' · ')}
              />
              <Row
                label="Local emergency contact"
                value={[register.localEmergencyContact.name, register.localEmergencyContact.phone]
                  .filter(Boolean)
                  .join(' · ')}
              />
            </dl>
            <dl className="divide-y divide-border">
              <Row
                label="Vet"
                value={[register.vet.practiceName, register.vet.vetName, register.vet.phone]
                  .filter(Boolean)
                  .join(' · ')}
              />
              <Row
                label="Insurance"
                value={[register.insurance.provider, register.insurance.policyNumber]
                  .filter(Boolean)
                  .join(' · ')}
              />
              <Row label="Medical history" value={register.medical.conditions} />
              <Row label="Behaviour" value={register.medical.behaviour} />
              <Row
                label="Diet"
                value={register.diet.dietNotes ?? register.diet.feedingInstructions}
              />
              <Row
                label="Last worming"
                value={formatDate(register.parasiteTreatment.wormingLastOn)}
              />
              <Row
                label="Last flea treatment"
                value={formatDate(register.parasiteTreatment.fleaLastOn)}
              />
              <Row label="Vet consent" value={formatDate(register.consents.vetConsentGivenAt)} />
              <Row
                label="Consent form"
                value={
                  register.consents.waiverAcceptedAt
                    ? `v${register.consents.waiverVersion ?? '?'} · ${formatDate(register.consents.waiverAcceptedAt)}`
                    : null
                }
              />
              <Row
                label="Vaccinations"
                value={register.vaccinations
                  .map(
                    (v) =>
                      `${v.vaccineName} (${v.expiresOn ? formatDate(v.expiresOn) : 'no expiry'})`,
                  )
                  .join(', ')}
              />
            </dl>
          </div>

          {register.stays.length > 0 && (
            <div className="mt-4">
              <h3 className="mb-1.5 text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
                Arrivals and departures
              </h3>
              <ul className="divide-y divide-border text-sm">
                {register.stays.slice(0, 10).map((s) => (
                  <li
                    key={s.bookingId}
                    className="num flex items-center justify-between gap-3 py-1.5"
                  >
                    <span>
                      {formatDate(s.arrival)} → {formatDate(s.actualDeparture ?? s.departure)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {s.runName ?? ''}
                      {s.runName ? ' · ' : ''}
                      {s.status.replace('_', ' ')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
